import { useCallback, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "react-use";

import useDefaultErrorWrapper from "../../../hooks/useDefaultErrorWrapper";
import getAppointmentData from "./getAppointmentData";
import { Appointment } from "./types";
import useTranslations from "./useTranslations";
import supabaseClient from "../../../common/supabaseClient";

const useAppointmentData = (appointmentId?: string) => {
  const { runAsyncFunction } = useDefaultErrorWrapper();
  const { translations } = useTranslations();
  const { appointmentNotFound } = translations;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [data, setData] = useState<Appointment | null>(null);

  // Track in-flight requests to prevent race conditions
  const fetchCounterRef = useRef(0);
  const isFetchingRef = useRef(false);

  const fetch = useCallback(async () => {
    if (!appointmentId) return;

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    // Increment counter for this fetch attempt
    fetchCounterRef.current += 1;
    const currentFetchId = fetchCounterRef.current;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const appointmentResult = await runAsyncFunction(
        getAppointmentData,
        appointmentId,
      );

      // Only update state if this is still the latest fetch
      if (currentFetchId === fetchCounterRef.current) {
        if (!appointmentResult) {
          throw new Error(appointmentNotFound);
        }

        setData(appointmentResult);
        setErrorMessage(null);
      }
    } catch (error) {
      // Only update error state if this is still the latest fetch
      if (currentFetchId === fetchCounterRef.current) {
        setErrorMessage((error as Error).message);
        setData(null);
      }
    } finally {
      // Only update loading state if this is still the latest fetch
      if (currentFetchId === fetchCounterRef.current) {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }
  }, [appointmentId, appointmentNotFound, runAsyncFunction]);

  // Store fetch in a ref to avoid recreating subscriptions
  const fetchRef = useRef(fetch);

  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  // Every customer/patient id referenced by the currently loaded appointment
  // (booker, guardian, other-person, every patient row) — kept in a ref so
  // the realtime subscription below (which only re-subscribes on
  // appointmentId change, not on every data refresh) can always check
  // against the latest set without needing to be recreated.
  const relevantMergeIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const ids = new Set<string>();
    data?.appointment_customer_details?.forEach((acd) => {
      if (acd.customer?.id) ids.add(acd.customer.id);
    });
    data?.appointment_patient_details?.forEach((apd) => {
      if (apd.customer?.id) ids.add(apd.customer.id);
      if (apd.patient_id) ids.add(apd.patient_id);
    });
    relevantMergeIdsRef.current = ids;
  }, [data]);

  useEffectOnce(() => {
    const getAppointment = async () => {
      await fetch();
    };

    getAppointment();
  });

  useEffect(() => {
    if (!appointmentId) return;

    const appointmentSubscription = supabaseClient
      .channel(`appointment-changes-${appointmentId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointments" },
        async (payload) => {
          if (
            payload.new.id === appointmentId &&
            (payload.new.status !== payload.old.status ||
              payload.new.has_priority !== payload.old.has_priority)
          ) {
            await fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointment_offerings" },
        async (payload) => {
          if (
            payload.new.appointment_id === appointmentId &&
            payload.new.status !== payload.old.status
          ) {
            await fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "appointment_refunds" },
        async (payload) => {
          if (payload.new.appointment_id === appointmentId) {
            await fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "appointment_receipts" },
        async (payload) => {
          if (payload.new.appointment_id === appointmentId) {
            await fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointment_receipts" },
        async (payload) => {
          if (payload.new.appointment_id === appointmentId) {
            await fetchRef.current();
          }
        },
      )
      // Merge-lock: refetch when a linked customer/patient's merge_status
      // changes (e.g. a merge starts on this appointment's customer/patient
      // while this screen is already open). Both tables have REPLICA
      // IDENTITY DEFAULT, so payload.old only carries the PK — there's no
      // old value to diff against, so this always refetches on any UPDATE to
      // a relevant row (same degrade-to-always-refetch tradeoff already
      // accepted above for `appointments`/`appointment_offerings`).
      // Requires customers/patients to be added to the supabase_realtime
      // publication (see kudzu-data migration 20260708090100).
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "customers" },
        async (payload) => {
          if (relevantMergeIdsRef.current.has(payload.new.id as string)) {
            await fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "patients" },
        async (payload) => {
          if (relevantMergeIdsRef.current.has(payload.new.id as string)) {
            await fetchRef.current();
          }
        },
      )
      .subscribe();

    // eslint-disable-next-line consistent-return
    return () => {
      supabaseClient.removeChannel(appointmentSubscription);
    };
  }, [appointmentId]);

  return {
    isLoading,
    errorMessage,
    data,
    refetch: fetch,
  };
};

export default useAppointmentData;
